import { useEffect, useRef, useState } from 'react'
import type { JSX } from 'react'
import { useScreenTab } from '../../shared/screen-tab.ts'
import { useTranslation } from 'react-i18next'
import { formatDateTimeVN, formatDateVN } from '@rs/contracts'
import { Badge, Button, Card, DateInput, Modal, TabPill } from '@rs/ui'
import { fmtStopAt, stopAddr, toStops, type Stop } from '../az-sales/order/stops.ts'
import { addDays, addMonths, isoOf, monthGrid, startOfWeek, type Gran } from '../az-ops/trip-calendar.ts'
import { confirmPlannedApi, type MyLegRow, type MyPlannedJob } from './api.ts'
import { useCan } from '../../shared/can.ts'

/**
 * ══ CỔNG THẦU PHỤ ▸ 📋 KẾ HOẠCH — chép `NccPortal.tsx:476-700` ═════════════════════════════════
 *
 * ⭐ HAI KIỂU XEM chép ĐÚNG hai tab nội bộ (bản cũ user 27/08: "render nó giống y 100% cái tab kế
 *   hoạch thầu phụ và lịch chạy trong Điều hành ĐH"), dữ liệu là chặng CỦA RIÊNG thầu phụ này:
 *   · 📅 Kế hoạch thầu phụ — lưới Ngày / Tuần / Tháng, mỗi ô liệt kê thẳng MÃ ĐƠN (bản nội bộ có
 *     chip NCC vì xem nhiều NCC một lúc; ở đây luôn là chính họ);
 *   · 📅 Lịch chạy — bảng 7 ngày kể từ một ngày, gom theo đơn, bấm đơn xem các chặng.
 * ⭐ Bên dưới là ĐƠN DỰ KIẾN GIAO CHO TÔI (Đối tác dự kiến = mình, CHƯA bắn chặng) kèm "✓ Xác nhận
 *   trước" từng đơn hoặc hàng loạt. ⚠ Đó CHỈ là tín hiệu đồng ý trước — không tạo chặng, không chốt
 *   giá; công ty vẫn bắn chặng chính thức sau như thường.
 * ⚠ Thay bảng `KỲ · TUYẾN · KẾ HOẠCH · ĐÃ CHẠY` (F31) từng đứng ở đây: bản cũ không có bảng đó trên
 *   cổng (DIEM-CAN-QUYET-DINH C23 ②). Cửa `/api/v1/ncc/ke-hoach` của F31 vẫn còn ở máy chủ.
 */

const WEEKDAYS = ['T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'CN'] as const
type Weekday = (typeof WEEKDAYS)[number]

/** Chuyến ĐÃ THỰC HIỆN — NCC đã xác nhận hoặc chặng đã lăn bánh (bản cũ `legExecuted`). */
const executed = (l: MyLegRow): boolean => l.vendorConfirmed || (l.status !== 'new' && l.status !== 'assigned')

const todayIso = (): string => isoOf(new Date())

const stopText = (s: Stop | undefined): string =>
  [s?.station, stopAddr(s) === s?.station ? '' : stopAddr(s), s?.contact, s?.phone]
    .filter((x): x is string => x !== undefined && x.trim() !== '').join(' · ')

export const PlanTab = ({ legs, planned, onChanged }: {
  legs: readonly MyLegRow[]
  planned: readonly MyPlannedJob[]
  onChanged: () => void
}): JSX.Element => {
  const { t } = useTranslation()
  const today = todayIso()
  const canDo = useCan()
  const [view, setView, viewShown] = useScreenTab<'cal' | 'board'>('vendorPortal.plan', { fallback: 'cal' })
  const [gran, setGran] = useState<Gran>('month')
  const [from, setFrom] = useState(() => `${today.slice(0, 8)}01`)
  const [mode, setMode, modeShown] = useScreenTab<'plan' | 'done'>('vendorPortal.plan.cal', { fallback: 'plan' })
  const [fCont, setFCont] = useState('')
  const [dayOpen, setDayOpen] = useState<string | null>(null)
  const [pickOpen, setPickOpen] = useState(false)
  // Bấm RA NGOÀI ô chọn kỳ thì đóng — `pickRef` + mousedown như bản cũ (`NccPortal.tsx:69-74`, V03965 · V04113).
  const pickRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!pickOpen) return
    const h = (e: MouseEvent): void => { if (pickRef.current !== null && !pickRef.current.contains(e.target as Node)) setPickOpen(false) }
    document.addEventListener('mousedown', h)
    return () => { document.removeEventListener('mousedown', h) }
  }, [pickOpen])
  const [boardFrom, setBoardFrom] = useState(today)
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set())
  const [sel, setSel] = useState<ReadonlySet<string>>(new Set())
  const [err, setErr] = useState<string | null>(null)

  // Nhãn TĨNH (không ghép chuỗi) — thứ trong tuần · độ phóng · trạng thái chặng.
  const WD: Record<Weekday, string> = {
    T2: t('azOps.vendorPlan.weekday.T2'), T3: t('azOps.vendorPlan.weekday.T3'), T4: t('azOps.vendorPlan.weekday.T4'),
    T5: t('azOps.vendorPlan.weekday.T5'), T6: t('azOps.vendorPlan.weekday.T6'), T7: t('azOps.vendorPlan.weekday.T7'),
    CN: t('azOps.vendorPlan.weekday.CN'),
  }
  const GRAN: Record<Gran, string> = {
    day: t('azOps.vendorPlan.granDay'), week: t('azOps.vendorPlan.granWeek'), month: t('azOps.vendorPlan.granMonth'),
  }
  const ST: Record<MyLegRow['status'], string> = {
    new: t('az.st.new'), assigned: t('az.st.assigned'), at_pickup: t('az.st.at_pickup'), loaded: t('az.st.loaded'),
    in_transit: t('az.st.in_transit'), at_drop: t('az.st.at_drop'), delivered: t('az.st.delivered'),
    cancelled: t('az.st.cancelled'),
  }

  /* ── 📅 Lưới kế hoạch ─────────────────────────────────────────────────────────────────── */
  const contOpts = [...new Set(legs.map((l) => l.jobContType ?? '').filter((x) => x !== ''))].sort()
  const calLegs = legs.filter((l) => (fCont === '' || l.jobContType === fCont) && (mode === 'plan' || executed(l)))
  const legsOfDay = (iso: string): MyLegRow[] => calLegs.filter((l) => l.planDate === iso)
  const ym = from.slice(0, 7)
  const weekMon = startOfWeek(from)
  const inView = (iso: string): boolean => gran === 'month' ? iso.slice(0, 7) === ym
    : gran === 'week' ? iso >= weekMon && iso <= addDays(weekMon, 6) : iso === from
  const periodLabel = gran === 'month' ? t('azOps.vendorPlan.monthLabel', { m: ym.slice(5), y: ym.slice(0, 4) })
    : gran === 'week' ? t('azOps.vendorPlan.weekLabel', { from: formatDateVN(weekMon).slice(0, 5), to: formatDateVN(addDays(weekMon, 6)) })
      : formatDateVN(from)
  const yearOpts = [...new Set([...calLegs.map((l) => l.planDate.slice(0, 4)), today.slice(0, 4), ym.slice(0, 4)])].sort()
  const totalTrips = calLegs.filter((l) => inView(l.planDate)).length
  const anchorOf = (g: Gran, iso: string): string => g === 'month' ? `${iso.slice(0, 8)}01` : g === 'week' ? startOfWeek(iso) : iso
  const shift = (dir: number): void => {
    setFrom(gran === 'month' ? addMonths(`${ym}-01`, dir) : gran === 'week' ? startOfWeek(addDays(from, dir * 7)) : addDays(from, dir))
  }
  const goToday = (): void => { setFrom(anchorOf(gran, today)) }

  /** Ô MỘT NGÀY — hàm vẽ, không phải component con (khai component trong thân cha là dựng lại cả lưới mỗi lượt vẽ). */
  const dayCell = (iso: string, tall = false, square = false): JSX.Element => {
    const dls = legsOfDay(iso)
    const done = dls.filter(executed).length
    const pct = dls.length > 0 ? Math.round((done / dls.length) * 100) : 0
    const isToday = iso === today
    const max = tall ? 6 : 3
    return (
      <button type="button" onClick={() => { if (dls.length > 0) setDayOpen(iso) }}
              className={`w-full text-left rounded-md border px-1 py-0.5 ${square ? 'aspect-square' : tall ? 'min-h-[96px]' : 'min-h-[54px]'} overflow-hidden transition ${isToday ? 'border-brand-400 bg-brand-50/40' : 'border-slate-200 bg-white'} ${dls.length > 0 ? 'hover:border-brand-400 hover:shadow-sm cursor-pointer' : 'cursor-default'}`}>
        <div className="flex items-center justify-between leading-none">
          <span className="flex items-center gap-0.5">
            <span className={`text-[10px] font-bold ${isToday ? 'text-brand-600' : 'text-slate-400'}`}>{Number(iso.slice(8, 10))}</span>
            {dls.length > 0 && (pct === 100
              ? <span title={t('azOps.vendorPlan.allDoneTip')} className="text-[10px] font-bold text-emerald-600 leading-none">✓</span>
              : done > 0 ? <span title={t('azOps.vendorPlan.partialDoneTip', { done, total: dls.length })} className="text-[8px] font-bold text-amber-600 leading-none">{pct}%</span> : null)}
          </span>
        </div>
        <div className="mt-0.5 space-y-px">
          {dls.slice(0, max).map((l) => (
            <div key={l.id} title={l.jobRef} className="text-[8px] font-semibold rounded px-1 leading-[13px] border truncate bg-sky-100 text-sky-700 border-sky-200">{l.jobRef}</div>
          ))}
          {dls.length > max && <div className="text-[8px] text-slate-400 leading-[13px] px-1">+{dls.length - max}</div>}
        </div>
      </button>
    )
  }

  /* ── 📅 Lịch chạy 7 ngày — gom theo đơn ─────────────────────────────────────────────────── */
  const boardDays = Array.from({ length: 7 }, (_, i) => addDays(boardFrom, i))
  const boardOfDay = (d: string): { jobId: string; ref: string; contType: string | null; customer: string | null; jl: MyLegRow[] }[] => {
    const byJob = new Map<string, MyLegRow[]>()
    for (const l of legs) if (l.planDate === d) byJob.set(l.jobId, [...(byJob.get(l.jobId) ?? []), l])
    return [...byJob.entries()].map(([jobId, jl]) => ({
      jobId, ref: jl[0]?.jobRef ?? '', contType: jl[0]?.jobContType ?? null, customer: jl[0]?.customerName ?? null,
      jl: [...jl].sort((a, b) => a.seq - b.seq),
    }))
  }
  const weekOrders = boardDays.reduce((n, d) => n + boardOfDay(d).length, 0)
  const toggle = (k: string): void => {
    setExpanded((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n })
  }

  /* ── Đơn dự kiến giao cho tôi ─────────────────────────────────────────────────────────── */
  const pending = planned.filter((j) => !j.plannedVendorConfirmed)
  const confirmPlan = (ids: readonly string[], bulk: boolean): void => {
    setErr(null)
    confirmPlannedApi(ids, bulk).then(() => { setSel(new Set()); onChanged() },
      (e: unknown) => { setErr(e instanceof Error ? e.message : String(e)) })
  }

  return (
    <div className="space-y-2.5">
      <div className="flex gap-2">
        {viewShown('cal') && <TabPill active={view === 'cal'} onClick={() => { setView('cal') }}>{t('azOps.plan.tabLabel')}</TabPill>}
        {viewShown('board') && <TabPill active={view === 'board'} onClick={() => { setView('board') }}>{t('az.tabs.plan')}</TabPill>}
      </div>

      {view === 'cal' && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex gap-1.5">
              {(['day', 'week', 'month'] as const).map((g) => (
                <TabPill key={g} active={gran === g} onClick={() => { setGran(g); setFrom(anchorOf(g, today)) }}>{GRAN[g]}</TabPill>
              ))}
            </div>
            <div className="flex items-center gap-1">
              <button type="button" onClick={() => { shift(-1) }} className="w-7 h-7 rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50 font-bold">‹</button>
              <div className="relative" ref={pickRef}>
                <button type="button" onClick={() => { setPickOpen((o) => !o) }} title={t('azOps.vendorPlan.pickPeriodTip')}
                        className="text-[13px] font-bold text-slate-700 min-w-[150px] text-center px-2 py-0.5 rounded-md hover:bg-slate-100">
                  {periodLabel} <span className="text-[9px] text-slate-400">▾</span>
                </button>
                {pickOpen && (
                  <div className="absolute z-30 mt-1 left-1/2 -translate-x-1/2 bg-white border border-slate-200 rounded-xl shadow-xl p-2 text-[11px]">
                    {gran === 'month' ? (
                      <div className="flex items-center gap-1.5">
                        <select value={Number(ym.slice(5))} aria-label={t('azOps.vendorPlan.granMonth')}
                                onChange={(e) => { setFrom(`${ym.slice(0, 4)}-${String(Number(e.target.value)).padStart(2, '0')}-01`) }}
                                className="input !py-1 !text-[11px] !w-auto">
                          {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => <option key={m} value={m}>{t('azOps.vendorPlan.monthOption', { m })}</option>)}
                        </select>
                        <select value={Number(ym.slice(0, 4))} aria-label={t('azOps.vendorPlan.yearOption')}
                                onChange={(e) => { setFrom(`${e.target.value}-${ym.slice(5)}-01`) }}
                                className="input !py-1 !text-[11px] !w-auto">
                          {yearOpts.map((y) => <option key={y} value={y}>{y}</option>)}
                        </select>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5">
                        <span className="text-slate-400 font-semibold">{gran === 'week' ? t('azOps.vendorPlan.weekOfDayLabel') : t('azOps.vendorPlan.dayLabel')}</span>
                        <DateInput valueIso={from} onChangeIso={(d) => { if (d !== '') setFrom(gran === 'week' ? startOfWeek(d) : d) }}
                                   className="!py-1 !text-[11px] !w-[120px]" />
                      </div>
                    )}
                    <div className="flex items-center gap-2 mt-1.5 pt-1.5 border-t border-slate-100">
                      <button type="button" onClick={() => { goToday(); setPickOpen(false) }} className="font-semibold text-brand-600 hover:underline">{t('azOps.vendorPlan.backToNowBtn')}</button>
                      <button type="button" onClick={() => { setPickOpen(false) }} className="ml-auto font-semibold text-slate-400 hover:text-slate-600">{t('common.close')}</button>
                    </div>
                  </div>
                )}
              </div>
              <button type="button" onClick={() => { shift(1) }} className="w-7 h-7 rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50 font-bold">›</button>
              <button type="button" onClick={goToday} className="ml-1 text-[11px] font-semibold text-brand-600 hover:underline">{t('azOps.vendorPlan.todayBtn')}</button>
            </div>
            <div className="flex gap-1.5">
              {modeShown('plan') && <TabPill active={mode === 'plan'} onClick={() => { setMode('plan') }}>{t('azOps.vendorPlan.modePlan')}</TabPill>}
              {modeShown('done') && <TabPill active={mode === 'done'} onClick={() => { setMode('done') }}>{t('azOps.vendorPlan.modeDone')}</TabPill>}
            </div>
            {contOpts.length > 0 && (
              <select value={fCont} aria-label={t('azOps.vendorPlan.allContOption')} onChange={(e) => { setFCont(e.target.value) }}
                      className="border border-slate-200 rounded-lg px-2 py-1.5 text-[11px] min-w-[110px]">
                <option value="">{t('azOps.vendorPlan.allContOption')}</option>
                {contOpts.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            )}
            <div className="ml-auto flex items-center gap-1.5 text-[11px]">
              <span className="text-slate-400">{t('azOps.vendorPlan.totalPeriodPre')}<b className="text-slate-700">{totalTrips}</b>{t('azOps.vendorPlan.totalPeriodPost')}</span>
            </div>
          </div>

          {gran === 'month' ? (
            <div className="grid grid-cols-7 gap-1 w-full max-w-[760px] mx-auto">
              {WEEKDAYS.map((w) => <div key={w} className={`text-center text-[10px] font-bold pb-0.5 ${w === 'CN' ? 'text-red-400' : 'text-slate-400'}`}>{WD[w]}</div>)}
              {monthGrid(from).map((c) => c.inMonth
                ? <div key={c.iso}>{dayCell(c.iso, false, true)}</div>
                : <div key={c.iso} className="aspect-square rounded-md bg-slate-50/40" />)}
            </div>
          ) : gran === 'week' ? (
            <div className="grid grid-cols-7 gap-1">
              {WEEKDAYS.map((w, i) => (
                <div key={w} className={`text-center text-[10px] font-bold pb-1 ${w === 'CN' ? 'text-red-400' : 'text-slate-400'}`}>
                  {WD[w]} <span className="text-slate-300">{formatDateVN(addDays(weekMon, i)).slice(0, 5)}</span>
                </div>
              ))}
              {WEEKDAYS.map((w, i) => <div key={w}>{dayCell(addDays(weekMon, i), true)}</div>)}
            </div>
          ) : (
            <Card className="!p-0 overflow-hidden">
              <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-[12px] font-bold text-slate-700">
                {t('azOps.vendorPlan.dayTripsHeader', { date: formatDateVN(from), n: legsOfDay(from).length })}
              </div>
              {legsOfDay(from).length === 0
                ? <div className="text-center py-10 text-slate-300 italic text-xs">{t('azOps.vendorPlan.noTripsInDay')}</div>
                : (
                  <table className="w-full text-xs"><tbody>
                    {legsOfDay(from).map((l) => (
                      <tr key={l.id} className="border-t border-slate-50 first:border-0 hover:bg-slate-50 cursor-pointer" onClick={() => { setDayOpen(from) }}>
                        <td className="px-3 py-1.5 font-mono text-[11px] font-bold text-brand-600">{l.jobRef}</td>
                        <td className="px-3 py-1.5 text-slate-500 truncate max-w-[240px]" title={`${l.fromAddr} → ${l.toAddr}`}>{l.fromAddr} → {l.toAddr}</td>
                        <td className="px-3 py-1.5 text-right text-slate-500">{ST[l.status]}</td>
                      </tr>
                    ))}
                  </tbody></table>
                  )}
            </Card>
          )}

          {dayOpen !== null && (
            <Modal title={t('azOps.vendorPlan.dayTripsHeader', { date: formatDateVN(dayOpen), n: legsOfDay(dayOpen).length })}
                   onClose={() => { setDayOpen(null) }} className="max-w-lg">
              <div className="divide-y divide-slate-100">
                {legsOfDay(dayOpen).map((l) => (
                  <div key={l.id} className="py-2">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono font-bold text-brand-600 text-sm">{l.jobRef}</span>
                      <span className="text-[10px] font-bold text-slate-500 bg-slate-100 rounded px-1.5 py-0.5">{ST[l.status]}</span>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">{l.customerName ?? ''}</div>
                    <div className="text-xs text-slate-500">{l.fromAddr} → {l.toAddr}</div>
                  </div>
                ))}
              </div>
            </Modal>
          )}
        </div>
      )}

      {view === 'board' && (
        <div className="space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-semibold text-slate-500">{t('az.plan.from')}</span>
            <DateInput valueIso={boardFrom} onChangeIso={(d) => { if (d !== '') setBoardFrom(d) }} ariaLabel={t('az.plan.from')} />
            <div className="ml-auto flex items-center gap-1.5 text-[11px]">
              <span className="text-slate-400">{t('azOps.plan.weekCountPre')}<b className="text-slate-700">{weekOrders}</b>{t('azOps.plan.weekCountPost')}</span>
            </div>
          </div>
          <div className="grid md:grid-cols-4 lg:grid-cols-7 gap-2">
            {boardDays.map((d) => {
              const rows = boardOfDay(d)
              return (
                <Card key={d} className="p-2">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-bold text-slate-700">{formatDateVN(d).slice(0, 5)}</span>
                    <span className="text-[10px] text-slate-400">{t('az.sales.kpi.orders', { n: rows.length })}</span>
                  </div>
                  <div className="space-y-1">
                    {rows.length === 0 && <div className="text-center text-[10px] text-slate-300 italic py-3">{t('az.plan.empty')}</div>}
                    {rows.map((j) => {
                      const key = `${d}:${j.jobId}`
                      const open = expanded.has(key)
                      return (
                        <div key={j.jobId}>
                          <button type="button" onClick={() => { toggle(key) }} title={t('azOps.plan.viewLegsTip', { ref: j.ref })}
                                  className="w-full text-left rounded-md border px-1.5 py-1 text-[10px] leading-tight bg-white border-slate-200">
                            <div className="font-bold truncate flex items-center gap-1">
                              <span className="truncate font-mono">{j.ref}</span>
                              {j.contType !== null && (
                                <span className="shrink-0 font-mono font-bold text-[9px] text-sky-700 bg-sky-50 border border-sky-200 rounded px-1 py-px">{j.contType}</span>
                              )}
                            </div>
                            <div className="opacity-70 truncate flex items-center justify-between gap-1">
                              <span className="truncate">{j.customer ?? '—'}</span>
                              <span className="shrink-0 text-slate-500">{open ? '▾' : '▸'}{j.jl.length > 1 ? t('azOps.plan.legsCountSuffix', { n: j.jl.length }) : ''}</span>
                            </div>
                          </button>
                          {open && (
                            <div className="mt-0.5 ml-1 space-y-px border-l border-slate-200 pl-1.5">
                              {j.jl.map((l) => (
                                <div key={l.id} className="text-[9px] text-slate-500 truncate" title={`${l.fromAddr} → ${l.toAddr}`}>
                                  {t('az.leg.n', { n: l.seq })} · {ST[l.status]}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </Card>
              )
            })}
          </div>
        </div>
      )}

      {/* ── Đơn có "Đối tác dự kiến" = tôi, CHƯA bắn chặng — xác nhận nhận trước ── */}
      <p className="text-[11px] text-slate-500 bg-sky-50 border border-sky-200 rounded-lg px-2.5 py-1.5">{t('ncc.planHint', { n: pending.length })}</p>
      {err !== null && <div role="alert" className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{err}</div>}
      {planned.length === 0 && <Card className="p-6 text-center text-slate-400 italic text-sm">{t('ncc.emptyPlan')}</Card>}
      {pending.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <button type="button" className="text-[11px] font-semibold text-brand-600 hover:underline"
                  onClick={() => { setSel(new Set(sel.size === pending.length ? [] : pending.map((j) => j.id))) }}>
            {sel.size === pending.length ? t('ncc.unselectAll') : t('ncc.selectAll')}
          </button>
          <Button size="sm" disabled={sel.size === 0 || !canDo('vendorPortal.plan', 'edit')} onClick={() => { confirmPlan([...sel], true) }}>{t('ncc.confirmBulkBtn', { n: sel.size })}</Button>
        </div>
      )}
      {planned.map((j) => {
        const pu = toStops(j.pickups)[0]
        const dr = toStops(j.drops)[0]
        return (
          <Card key={j.id} className="p-3.5">
            <div className="flex items-start justify-between gap-2 flex-wrap">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono font-bold text-violet-600">{j.ref}</span>
                  {j.routeName !== null && j.routeName !== '' && <Badge className="bg-slate-100 text-slate-600">{j.routeName}</Badge>}
                  {j.contType !== null && <Badge className="bg-sky-100 text-sky-700">{j.contType}</Badge>}
                </div>
                <div className="text-sm font-semibold text-slate-800 mt-1">
                  {j.cargo ?? t('ncc.transport')}{j.weightTon !== null && j.weightTon > 0 ? ` · ${j.weightTon.toLocaleString('vi-VN')} ${t('ncc.ton')}` : ''}
                </div>
              </div>
              {j.plannedVendorConfirmed
                ? <span className="text-[11px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-2.5 py-1 whitespace-nowrap">{t('ncc.planConfirmed')}</span>
                : (
                  <div className="flex items-center gap-1.5 flex-wrap justify-end">
                    <input type="checkbox" checked={sel.has(j.id)} aria-label={j.ref}
                           onChange={(e) => {
                             const on = e.target.checked
                             setSel((s) => { const n = new Set(s); if (on) n.add(j.id); else n.delete(j.id); return n })
                           }} />
                    <Button size="sm" disabled={!canDo('vendorPortal.plan', 'edit')} onClick={() => { confirmPlan([j.id], false) }}>{t('ncc.planConfirmBtn')}</Button>
                  </div>
                  )}
            </div>
            <div className="mt-2 grid sm:grid-cols-2 gap-2 text-xs">
              <div className="rounded-lg bg-emerald-50/60 border border-emerald-100 px-2.5 py-1.5">
                <div className="text-[10px] font-bold text-emerald-700 uppercase">
                  {t('ncc.pickup')} {pu?.at !== undefined ? `· ${fmtStopAt(pu.at)}` : j.planDate !== null ? `· ${formatDateVN(j.planDate)}` : ''}
                </div>
                <div className="text-slate-600 mt-0.5">{stopText(pu) || j.pickupAddr || '—'}</div>
              </div>
              <div className="rounded-lg bg-orange-50/60 border border-orange-100 px-2.5 py-1.5">
                <div className="text-[10px] font-bold text-orange-700 uppercase">
                  {t('ncc.drop')} {dr?.at !== undefined ? `· ${fmtStopAt(dr.at)}` : ''}
                </div>
                <div className="text-slate-600 mt-0.5">{stopText(dr) || j.dropAddr || '—'}</div>
              </div>
            </div>
            {j.plannedVendorConfirmed && j.plannedVendorConfirmedAt !== null && (
              <div className="mt-1 text-[10px] text-emerald-600">{t('ncc.confirmedAt', { time: formatDateTimeVN(j.plannedVendorConfirmedAt) })}</div>
            )}
          </Card>
        )
      })}
    </div>
  )
}
